/**
 * 统一文件上传服务
 * 分离UI交互和接口交互，提供统一的文件上传API
 */

import { attachmentApi } from '@/api/api'
import { isImageFile, prepareImageForUpload, getImageDimensions } from '@/utils/imageCompressor'
import { normalizeMimeType, getFileTypeCode as getFileTypeCodeFromMime } from '@/utils/mimeTypeUtils'
import type { PresignUploadRequest, PresignUploadData, CommitUploadRequest, CommitUploadData, EntityLink } from '@/types/upload'
import type { CompressOptions } from '@/utils/imageCompressor'
import { putToS3 } from './s3-put'
import { resolveS3UploadUrl } from './s3-upload-url'

// 文件类型映射
export type FileTypeCode = 'image' | 'audio' | 'pdf' | 'video' | 'document'

// 上传配置
export interface UploadConfig {
  entityType?: string
  entityId?: string
  categoryCode?: string
  sourceModule?: string
  /**
   * uploadMultipleFiles 关联到 entityId 时第一张的 seqNo（默认 1，之后逐张 +1）。
   * 同一实体分几批上传时传上一批之后的序号，排序不会从 1 重来。
   */
  seqStart?: number
  /**
   * 附件可见性（presign 与 commit 必须传同一个值 —— 后端会校验 s3Key 的
   * visibility 段与 commit 的 isPublic 一致）：
   *  - `true`（缺省）→ 公开对象，originalUrl 是可直连的公开 URL。
   *  - `false`       → 私有对象（s3Key 带 private 段），匿名 GET 403；
   *                    读取走后端签发的 presigned URL（commit 响应与
   *                    by-entities 读路径均 is_public 感知，1h 过期）。
   */
  isPublic?: boolean
  /** 图片压缩开关 — 由 FileUpload 等组件在构造 config 时携带,service 端 presign/commit 不消费。 */
  enableCompression?: boolean
  /** 图片压缩参数(imageCompressor CompressOptions),同上仅组件侧消费。 */
  compressionOptions?: CompressOptions
  /** 缩略图生成开关,同上仅组件侧消费。 */
  enableThumbnail?: boolean
  /**
   * 照片体系改造(2026-08-17 走查 §一,2026-08-20 扩到 product/inventory):
   * true 时把 entityLink(entityType/entityId/categoryCode)直接放进 COMMIT
   * 体,一次调用建 attachment 行 + entity link——后端镜像钩子随即把 URL 追加
   * 进对应实体的 photos 列。旧 'attachment' 模式的「commit 后单独 POST
   * entity-link」两段式不受影响(它不设此标志)。
   */
  linkOnCommit?: boolean
  /**
   * 中止信号,透传给 S3 PUT(putToS3)。abort 后 uploadSingleFile 跳过 commit
   * 并返回 { success:false, errorCode:'ABORTED' }。presign 已发出的那一步不
   * 可中止(axios 请求很短),不影响语义。
   */
  signal?: AbortSignal
}

// 文件处理结果
export interface ProcessedFile {
  originalFile: File
  processedFile: File
  compressionInfo?: {
    originalSize: number
    compressedSize: number
    compressionRatio: number
  }
}

// 上传结果
export interface UploadResult {
  success: boolean
  attachmentId?: string
  publicUrl?: string
  s3Key?: string
  error?: string
  errorCode?: string
  /**
   * Entity-link outcome (only meaningful when an entityId was provided):
   *  - `true`      → the attachment was successfully linked to the entity.
   *  - `false`     → the file uploaded/committed fine but the entity-link
   *                  failed (the photo is now orphaned — no page can show it,
   *                  so callers should surface this as a failure to the user).
   *  - `undefined` → no link was attempted this run (draft / upload-only
   *                  scenarios with no entityId).
   */
  linked?: boolean
}

// 上传进度回调
export type ProgressCallback = (progress: number, message?: string) => void

/**
 * 获取文件类型代码
 * @param mimeType 原始MIME类型
 * @param fileName 文件名（可选，用于MIME类型推断）
 */
export function getFileTypeCode(mimeType: string, fileName?: string): FileTypeCode {
  // 使用标准化的MIME类型
  const normalizedMimeType = normalizeMimeType(mimeType, fileName)
  return getFileTypeCodeFromMime(normalizedMimeType)
}

/**
 * 处理文件（压缩图片为 WebP）
 */
export async function processFile(
  file: File,
  _config: UploadConfig = {},
  onProgress?: ProgressCallback
): Promise<ProcessedFile> {
  onProgress?.(10, 'Processing file...')

  const processedFile = await prepareImageForUpload(file)

  const compressionInfo = isImageFile(file) && processedFile !== file && file.size > 0
    ? {
        originalSize: file.size,
        compressedSize: processedFile.size,
        compressionRatio: processedFile.size / file.size,
      }
    : undefined

  onProgress?.(100, 'Processing complete')
  return { originalFile: file, processedFile, compressionInfo }
}

/**
 * 上传单个文件
 */
export async function uploadSingleFile(
  processedFile: ProcessedFile,
  config: UploadConfig,
  onProgress?: ProgressCallback
): Promise<UploadResult> {
  try {
    const { originalFile, processedFile: file } = processedFile
    const { entityType, sourceModule } = config

    onProgress?.(10, 'Requesting upload URL...')

    const uploadMimeType = isImageFile(originalFile)
      ? file.type
      : normalizeMimeType(originalFile.type, originalFile.name)
    const uploadFileName = file.name

    // presign / commit 必须传同一个 isPublic（后端 _validate_s3_key_format
    // 会校验 s3Key visibility 段与 commit 值一致）
    const isPublic = config.isPublic ?? true

    const presignRequest: PresignUploadRequest = {
      fileName: uploadFileName,
      mimeType: uploadMimeType,
      attachmentFileTypeCode: getFileTypeCode(originalFile.type, originalFile.name),
      sourceModule: sourceModule || entityType || 'unknown',
      isPublic,
    }

    // axios-config 拦截器已解包信封，运行时返回的就是 data 部分（api 层类型仍是 AxiosResponse）
    const presignResponse = await attachmentApi.requestPresignUrl(presignRequest) as unknown as PresignUploadData
    const { uploadOriginalUrl, s3Key } = presignResponse

    onProgress?.(30, 'Uploading file...')

    // Step 2: upload to S3
    const finalUploadUrl = resolveS3UploadUrl(uploadOriginalUrl)

    const uploadHeaders: Record<string, string> = {
      'Content-Type': uploadMimeType,
      'Content-Disposition': 'inline',
      'x-amz-server-side-encryption': 'AES256',
    }
    // 私有上传时后端把 x-amz-acl 签进了 presigned URL 的 SignedHeaders，
    // PUT 缺这个头 S3 会 403 SignatureDoesNotMatch（mobile inventory
    // photoUpload.ts 踩过的同一个坑，此为已实测的解法）。
    if (isPublic === false) {
      uploadHeaders['x-amz-acl'] = 'private'
    }

    await putToS3(finalUploadUrl, file, uploadHeaders, {
      signal: config.signal,
      onProgress: (loaded, total) => {
        if (!total) return
        // 字节进度映射进既有的 30→80 里程碑区间(10-30 是 presign,80-100 是 commit),
        // 现有调用方看到的只是更平滑的数字,回调契约不变。
        onProgress?.(30 + Math.round((loaded / total) * 50), 'Uploading file...')
      },
    })

    onProgress?.(80, 'Committing attachment...')

    // 图片尺寸：后端不做服务端探测（无 Pillow、commit 也不 head_object），
    // 客户端不传 width/height 这两列就永久为 NULL，大图预览的分辨率也就显示不出来。
    // 量的是最终上传的那个 file（压缩后的 WebP），跟 sizeBytes 口径一致。
    // 测不出来（解码失败等）不阻塞上传，留空即可。
    let dimensions: { width: number; height: number } | null = null
    if (isImageFile(file)) {
      try {
        dimensions = await getImageDimensions(file)
      } catch (error) {
        console.warn('Failed to read image dimensions; committing without them:', error)
      }
    }

    // Step 4: commit attachment
    const commitRequest: CommitUploadRequest = {
      s3Key,
      previewKey: null,
      fileName: uploadFileName,
      attachmentFileTypeCode: getFileTypeCode(originalFile.type, originalFile.name),
      mimeType: uploadMimeType,
      sizeBytes: file.size,
      ...(dimensions ? { width: dimensions.width, height: dimensions.height } : {}),
      isPublic,
      sourceModule: sourceModule || entityType,
      // linkOnCommit: 单次调用建行+链接,并触发后端 photos 镜像钩子。
      ...(config.linkOnCommit && config.entityType && config.entityId
        ? {
            entityLink: {
              entityType: config.entityType,
              // UploadConfig 收的是 string;EntityLink 的 entityId 是品牌化
              // SnowflakeId,值本身就是后端发的雪花串,断言安全。
              entityId: config.entityId as EntityLink['entityId'],
              categoryCode: config.categoryCode ?? `${config.entityType}_photo`,
            },
          }
        : {}),
    }

    // 同上：拦截器解包后即 CommitUploadResponse
    const commitResponse = await attachmentApi.commitAttachment(commitRequest) as unknown as CommitUploadData

    onProgress?.(100, 'Upload complete')

    return {
      success: true,
      attachmentId: commitResponse.id,
      publicUrl: commitResponse.originalUrl,
      s3Key: commitResponse.s3Key
    }
  } catch (error) {
    // abort 是用户主动移除待发附件,不是错误:不打 error 日志,也不 commit。
    if ((error as { name?: string } | null)?.name === 'AbortError') {
      return { success: false, error: 'Upload aborted', errorCode: 'ABORTED' }
    }
    console.error('File upload failed:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
      errorCode: 'UPLOAD_FAILED'
    }
  }
}

/**
 * 关联附件到实体
 */
export async function linkAttachmentToEntity(
  attachmentId: string,
  config: {
    entityType: string
    entityId: string
    categoryCode: string
    seqNo?: number
    caption?: string
  }
): Promise<void> {
  const linkRequest = {
    entityType: config.entityType,
    entityId: config.entityId,
    categoryCode: config.categoryCode,
    seqNo: config.seqNo || 1,
    caption: config.caption || '',
    attachmentId: attachmentId
  }

  await attachmentApi.entityLinkAttachment(linkRequest)
}

/**
 * 批量上传文件
 */
export async function uploadMultipleFiles(
  files: File[],
  config: UploadConfig,
  onProgress?: (fileIndex: number, fileProgress: number, fileName: string) => void,
  onFileComplete?: (result: UploadResult, fileIndex: number) => void
): Promise<UploadResult[]> {
  const results: UploadResult[] = []

  for (let i = 0; i < files.length; i++) {
    const file = files[i]

    try {
      // 处理文件
      const processedFile = await processFile(file, config, (progress) => {
        onProgress?.(i, progress * 0.5, file.name) // 处理阶段占50%
      })

      // 上传文件
      const result = await uploadSingleFile(processedFile, config, (progress) => {
        onProgress?.(i, 50 + progress * 0.5, file.name) // 上传阶段占50%
      })

      results.push(result)
      onFileComplete?.(result, i)

      // 对于所有附件，需要调用 linkAttachmentToEntity 建立关联
      // `results.push(result)` 发生在 link 之前 —— 这里改的是同一个对象引用，
      // 所以对 result.linked 的赋值会直接反映到已入队的元素上。
      if (result.success && result.attachmentId && config.entityId) {
        try {
          await linkAttachmentToEntity(result.attachmentId, {
            entityType: config.entityType || 'task',
            entityId: config.entityId,
            categoryCode: config.categoryCode || 'attachment',
            seqNo: (config.seqStart ?? 1) + i
          })
          result.linked = true
        } catch (error) {
          console.error('Failed to link attachment:', error)
          // 上传成功但 link 失败 —— 照片成孤儿，标记出来让调用方提示用户
          result.linked = false
        }
      }
    } catch (error) {
      const errorResult: UploadResult = {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
        errorCode: 'UPLOAD_FAILED'
      }
      results.push(errorResult)
      onFileComplete?.(errorResult, i)
    }
  }

  return results
}

/**
 * 延迟上传函数生成器
 * 用于create-task-modal等需要延迟上传的场景
 */
export function createPendingUploadFunction(
  files: File[],
  config: Omit<UploadConfig, 'entityId'>,
  onProgress?: (fileIndex: number, fileProgress: number, fileName: string) => void
) {
  return async (entityId: string): Promise<UploadResult[]> => {
    const finalConfig = { ...config, entityId }
    return uploadMultipleFiles(files, finalConfig, onProgress)
  }
}

/**
 * Upload a single file and return the result (no entity-link created).
 * Use when you want to upload first and associate via API field later
 * (e.g. checklist referenceAttachments).
 */
export async function uploadFileOnly(file: File, compress?: CompressOptions): Promise<UploadResult> {
  if (!compress) return uploadSingleFile(await processFile(file), {})
  // 指定压缩档（建产品的照片 / 截图）
  const processedFile = await prepareImageForUpload(file, compress)
  return uploadSingleFile({ originalFile: file, processedFile }, {})
}

/**
 * 照片体系改造:上传并把附件直接 COMMIT 到实体(entityLink 随 commit 体走,
 * 单次调用)。后端镜像钩子会把公网 URL 追加进对应实体的 photos 列,调用方
 * 之后 refetch 实体即可,不要再自己 PATCH photos(避免双写竞态)。
 */
export async function uploadFileAndCommit(
  file: File,
  target: { entityType: string; entityId: string; categoryCode: string },
): Promise<UploadResult> {
  const processed = await processFile(file)
  return uploadSingleFile(processed, {
    entityType: target.entityType,
    entityId: target.entityId,
    categoryCode: target.categoryCode,
    sourceModule: target.entityType,
    linkOnCommit: true,
  })
}